import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { signIn, signUp, signOut, getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { User } from '../types';
import { userAPI } from '../services/api';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  signup: (username: string, password: string, email: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to generate a display name from user attributes
const getDisplayName = (userAttributes: any): string => {
  console.log('getDisplayName called with attributes:', userAttributes);
  
  const email = userAttributes?.email?.trim();
  const firstName = userAttributes?.given_name?.trim();
  const lastName = userAttributes?.family_name?.trim();
  
  console.log('Extracted values - email:', email, 'firstName:', firstName, 'lastName:', lastName);
  
  // Priority 1: Use first name from Cognito if available
  if (firstName && firstName.length > 0) {
    console.log('Using firstName:', firstName);
    return firstName;
  }
  
  // Priority 2: Use last name if available  
  if (lastName && lastName.length > 0) {
    console.log('Using lastName:', lastName);
    return lastName;
  }
  
  // Priority 3: Extract name from email (this should work for chris@frickland.com)
  if (email && email.includes('@')) {
    const emailPrefix = email.split('@')[0];
    if (emailPrefix && emailPrefix.length > 0) {
      // Capitalize first letter: chris -> Chris
      const capitalizedName = emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1).toLowerCase();
      console.log('Using email prefix as name:', capitalizedName);
      return capitalizedName;
    }
  }
  
  // Final fallback
  console.log('Using fallback name: User');
  return 'User';
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Effect to check initial auth state
  useEffect(() => {
    const checkAuthState = async () => {
      try {
        setIsLoading(true);
        
        // Force clear any cached user data that looks like an ID or has suspicious name patterns
        const savedUserString = sessionStorage.getItem('deckforge_user');
        if (savedUserString) {
          try {
            const savedUser = JSON.parse(savedUserString);
            const isUUIDName = savedUser?.name && savedUser.name.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
            const isLongIdLike = savedUser?.name && savedUser.name.length > 20 && savedUser.name.includes('-');
            
            if (isUUIDName || isLongIdLike) {
              console.log('Clearing cached user with ID-like name:', savedUser.name);
              sessionStorage.removeItem('deckforge_user');
            }
          } catch (e) {
            console.log('Invalid cached user data, clearing');
            sessionStorage.removeItem('deckforge_user');
          }
        }
        
        // Try to get current user from Cognito
        try {
          const cognitoUser = await getCurrentUser();
          const userAttributes = await fetchUserAttributes();
          
          // Create user from Cognito attributes
          console.log('Cognito user attributes:', userAttributes);
          const displayName = getDisplayName(userAttributes);
          
          // Double-check that we don't use a UUID as the display name
          const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(displayName);
          const finalDisplayName = isUUID ? 'User' : displayName;
          
          console.log('Final display name:', finalDisplayName, 'Was UUID:', isUUID);
          
          let authenticatedUser: User = {
            username: cognitoUser.username,
            email: userAttributes.email || '',
            name: finalDisplayName,
            subscription: 'free',
            presentationsUsed: 0,
            joinDate: new Date().toISOString().split('T')[0]
          };
          
          // Try to get full user profile with subscription data from backend
          try {
            const { data } = await userAPI.getProfile();
            authenticatedUser = {
              ...authenticatedUser,
              subscription: (data.subscription as 'free' | 'basic' | 'pro' | 'enterprise') || 'free',
              presentationsUsed: data.presentationsUsed || 0,
              joinDate: data.joinDate || authenticatedUser.joinDate
            };
          } catch (profileError) {
            console.log('Could not fetch user profile from backend, using defaults:', profileError);
          }
          
          console.log('Created user object:', authenticatedUser);
          setUser(authenticatedUser);
          sessionStorage.setItem('deckforge_user', JSON.stringify(authenticatedUser));
        } catch (authError) {
          // Not authenticated with Cognito, check local storage for demo mode
          const savedUser = JSON.parse(sessionStorage.getItem('deckforge_user') || 'null');
          if (savedUser) {
            console.log('Using saved user from session storage:', savedUser);
            // Check if the saved user has an ID-like name, refresh if needed
            if (savedUser.name && savedUser.name.length > 20 && savedUser.name.includes('-')) {
              console.log('Detected ID-like name in saved user, clearing cache');
              sessionStorage.removeItem('deckforge_user');
              setUser(null);
            } else {
              setUser(savedUser);
            }
          } else {
            setUser(null);
          }
        }
      } catch (err) {
        console.error('Error checking auth state:', err);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthState();

    // Listen for authentication events
    const hubListener = (data: any) => {
      const { channel, payload } = data;
      if (channel === 'auth') {
        switch (payload.event) {
          case 'signedIn':
            console.log('Hub: User signed in');
            checkAuthState();
            break;
          case 'signedOut':
            console.log('Hub: User signed out');
            setUser(null);
            sessionStorage.removeItem('deckforge_user');
            break;
          default:
            break;
        }
      }
    };

    const unsubscribe = Hub.listen('auth', hubListener);

    // Cleanup
    return () => {
      unsubscribe();
    };
  }, []);

  // Login function
  const login = async (username: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Use demo/demo for local development if enabled
      if (process.env.REACT_APP_ENABLE_DEMO_MODE === 'true' && username === 'demo' && password === 'demo') {
        const demoUser: User = {
          username: 'demo',
          email: 'demo@deckforge.com',
          name: 'Demo User',
          subscription: 'pro',
          presentationsUsed: 12,
          joinDate: '2025-01-15'
        };
        setUser(demoUser);
        sessionStorage.setItem('deckforge_user', JSON.stringify(demoUser));
        return;
      }
      
      // Otherwise use Cognito
      await signIn({ username, password });
      
      // Get user data after successful login
      const cognitoUser = await getCurrentUser();
      const userAttributes = await fetchUserAttributes();
      
      const displayName = getDisplayName(userAttributes);
      
      // Double-check that we don't use a UUID as the display name
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(displayName);
      const finalDisplayName = isUUID ? 'User' : displayName;
      
      console.log('Login - Final display name:', finalDisplayName, 'Was UUID:', isUUID);
      
      let loggedInUser: User = {
        username: cognitoUser.username,
        email: userAttributes.email || '',
        name: finalDisplayName,
        subscription: 'free',
        presentationsUsed: 0,
        joinDate: new Date().toISOString().split('T')[0]
      };
      
      // Try to get full user profile with subscription data from backend
      try {
        const { data } = await userAPI.getProfile();
        loggedInUser = {
          ...loggedInUser,
          subscription: (data.subscription as 'free' | 'basic' | 'pro' | 'enterprise') || 'free',
          presentationsUsed: data.presentationsUsed || 0,
          joinDate: data.joinDate || loggedInUser.joinDate
        };
      } catch (profileError) {
        console.log('Could not fetch user profile from backend during login, using defaults:', profileError);
      }
      
      setUser(loggedInUser);
      sessionStorage.setItem('deckforge_user', JSON.stringify(loggedInUser));
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to login. Please check your credentials.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Signup function
  const signup = async (username: string, password: string, email: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Sign up with Cognito - use email as username when email alias is configured
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
          },
        },
      });
      
      // User will need to confirm their account before logging in
    } catch (err: any) {
      console.error('Signup error:', err);
      setError(err.message || 'Failed to create account. Please try again.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setIsLoading(true);
      
      // Try to logout from Cognito
      try {
        await signOut();
      } catch (signOutError) {
        // Ignore signOut errors - user might not be signed in with Cognito
        console.log('SignOut error (expected for demo users):', signOutError);
      }
      
      // Clear local storage
      sessionStorage.removeItem('deckforge_user');
      setUser(null);
    } catch (err: any) {
      console.error('Logout error:', err);
      setError(err.message || 'Failed to logout. Please try again.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh user data from API
  const refreshUser = async () => {
    try {
      setIsLoading(true);
      
      // Try to get current user from Cognito
      try {
        const cognitoUser = await getCurrentUser();
        const userAttributes = await fetchUserAttributes();
        
        const displayName = getDisplayName(userAttributes);
        
        // Double-check that we don't use a UUID as the display name
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(displayName);
        const finalDisplayName = isUUID ? 'User' : displayName;
        
        console.log('Refresh - Final display name:', finalDisplayName, 'Was UUID:', isUUID);
        
        let refreshedUser: User = {
          username: cognitoUser.username,
          email: userAttributes.email || '',
          name: finalDisplayName,
          subscription: 'free',
          presentationsUsed: 0,
          joinDate: new Date().toISOString().split('T')[0]
        };
        
        // Try to get full user profile with subscription data from backend
        try {
          const { data } = await userAPI.getProfile();
          refreshedUser = {
            ...refreshedUser,
            subscription: (data.subscription as 'free' | 'basic' | 'pro' | 'enterprise') || 'free',
            presentationsUsed: data.presentationsUsed || 0,
            joinDate: data.joinDate || refreshedUser.joinDate
          };
        } catch (profileError) {
          console.log('Could not fetch user profile from backend during refresh, using defaults:', profileError);
        }
        
        setUser(refreshedUser);
        sessionStorage.setItem('deckforge_user', JSON.stringify(refreshedUser));
      } catch (cognitoError) {
        // Try API if available
        try {
          const { data } = await userAPI.getProfile();
          setUser(data as unknown as User);
        } catch (apiError) {
          console.error('Error refreshing user data:', apiError);
          throw apiError;
        }
      }
    } catch (err: any) {
      console.error('Error refreshing user data:', err);
      setError(err.message || 'Failed to refresh user data.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    isAuthenticated: !!user,
    isLoading,
    user,
    login,
    signup,
    logout,
    refreshUser,
    error
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};